/**
 * 詳細画面の見出しの下に置くタブの並び。既定のタブには画面の問いの答えだけを置き、深掘りの情報を
 * ほかのタブへ移すための共通の部品（Dhik 2026-09-29「情報をタブで分けるとかも良い」）。
 *
 * 見た目は業務詳細が画面の中に直書きしていたタブをそのまま移した。タブ名の横の数は件数、点は
 * 案件モードでそのタブの中に変更があることを示す。並びの右端には「変更前と比較」のような操作を置ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 詳細画面のタブの並びを件数と案件の変更の印付きで描き、押されたタブを呼び手へ返す。
 * @graph-connects none
 */
import type { ReactNode } from 'react';

/** タブ 1 つ。 @graph-connects none */
export interface DetailTab<T extends string> {
  /** タブの識別子。 */
  id: T;
  /** 見える名前。 */
  label: string;
  /** 名前の横に添える件数。省けば出さない。 */
  count?: number;
  /** 案件モードでこのタブの中に変更があるか。true のときだけ印を出す。 */
  changed?: boolean;
}

/** `DetailTabs` の props。 @graph-connects none */
export interface DetailTabsProps<T extends string> {
  /** 並べるタブ。左から順に出す。 */
  tabs: readonly DetailTab<T>[];
  /** 選んでいるタブの識別子。 */
  value: T;
  /** タブが押されたときに、そのタブの識別子を受け取る。 */
  onChange: (id: T) => void;
  /** タブの並びの右端に置く操作。省けば何も置かない。 */
  trailing?: ReactNode;
}

/**
 * 詳細画面のタブの並びを描く。
 *
 * @param props 並べるタブ ・ 選んでいるタブ ・ 押されたときの受け手 ・ 右端の操作
 * @returns タブの並び
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 詳細画面のタブを件数と案件の変更の印付きで並べ、押されたタブの識別子を呼び手へ返す。
 * @graph-connects none
 */
export function DetailTabs<T extends string>({
  tabs,
  value,
  onChange,
  trailing,
}: DetailTabsProps<T>) {
  return (
    <div className="flex items-end gap-1 border-b border-[var(--color-border)]">
      <div role="tablist" className="flex gap-1">
        {tabs.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={value === entry.id}
            data-tab={entry.id}
            onClick={() => {
              onChange(entry.id);
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold ${value === entry.id ? 'border-[var(--color-text-primary)] text-[var(--color-text-primary)]' : 'border-transparent text-[var(--color-text-muted)]'}`}
          >
            {entry.label}
            {entry.count !== undefined && (
              <span className="ml-1 font-normal text-[var(--color-text-muted)]">{entry.count}</span>
            )}
            {entry.changed === true && (
              <span
                data-changed
                aria-hidden="true"
                className="ml-1 inline-block size-1.5 rounded-full bg-[var(--color-text-primary)] align-middle"
              />
            )}
          </button>
        ))}
      </div>
      {trailing !== undefined && <div className="ml-auto pb-1">{trailing}</div>}
    </div>
  );
}
